import { selectStageReviewerProgressEnabled } from './project-reviewer-progress.selectors';
import { selectScreeningInfoEnabled } from '@app/screening/screening-overview/screening-overview-statistics.selectors';
import { selectStageOverviewBundleEnabled } from '@app/stage/stage-overview/stage-overview-statistics.selectors';
import { selectScreeningOverviewEnabled } from './project-screening-overview.selectors';
import { selectProjectReviewerProgressEnabled } from '@app/project/project-overview/project-reviewer-progress.state';
import { RuntimeFeatureFlagsService } from '@core/services/runtime-feature-flags.service';
import { inject } from '@angular/core';
import {
  CanMatchFn,
  PartialMatchRouteSnapshot,
  Route,
  UrlSegment,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { mapFn, throwErrorExp } from '@core/actions/util';
import { selectEntityMap } from '@core/state/entities/entity.selectors';
import { RequestStatus } from '@core/signal-store-features/request-state.feature';
import { RequestStatus as RequestOutcome } from '@core/state/global-request-state/global-request-state.reducer';
import { entityKey } from '@core/state/entities/entity-name-map';
import {
  selectLoadedProjectId,
  selectLoadingStatusForProject,
  selectProjectIsLoading,
  selectProjectLoadingStateForProject,
  selectReviewerScreeningStatsStatus,
} from '@core/state/ui/project/project-ui.selectors';
import { LoadProjectRequest, PROJECT_LOAD_TIMEOUT_MS } from '@core/requests';
import { selectStageReviewerAnnotationStatsStatusMap } from '@core/state/ui/stage/stage-ui.selectors';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { v4 as uuid } from 'uuid';
import { combineLatest, filter, from, map, Observable, of, switchMap, take, tap, timeout } from 'rxjs';
import { projectUiActions } from '@core/state/ui/project/project-ui.actions';
import {
  ProjectMemberStatus,
  selectAreMembershipStatsLoadedOrLoadingForProject,
  selectAreProjectStatsLoadedOrLoadingForProject,
  selectAreReviewerStatsLoadedOrLoadingForProject,
  selectMemberStatusForProject,
  selectProjectById,
} from '@core/state/entities/project/project.selectors';
import { ProjectLoadingStatus } from '@core/state/ui/project/project-ui.reducer';
import { selectProjectDetailsForProject } from '@core/state/entities/project-details/project-details.selectors';

/**
 * True when the remaining URL segments resolve to the Project Overview route: the redirect-empty
 * path (`/projects/:projectId`, which redirects to `detail`) or `detail` explicitly, with nothing
 * beyond it. Mirrors how `project-statistics-route.guard.ts` classifies the Overview destination
 * by its `detail` route-config path, but this `CanMatchFn` runs during route matching, before a
 * route tree exists to walk — so it reads the raw segments instead.
 */
export function isProjectOverviewDestination(segments: UrlSegment[]): boolean {
  const rest = segments.slice(1);
  return rest.length === 0 || (rest.length === 1 && rest[0].path === 'detail');
}

export function isStageReviewDestination(segments: UrlSegment[]): boolean {
  return (
    segments[1]?.path === 'stage' &&
    (segments.length === 3 ||
      segments[3]?.path === 'review' ||
      segments[3]?.path === 'reconcile')
  );
}

/**
 * How long navigation waits for a project's load before the guard gives up: the project's loading
 * state becomes Error and the guard denies, as for a failed load (frontend bug-fix plan, PR-6, D5). The
 * load itself is bounded by the same time (`PROJECT_LOAD_TIMEOUT_MS`), so a later navigation can load the
 * project again rather than being ignored behind a request that never answered.
 */
export const PROJECT_GUARD_WAIT_MS = PROJECT_LOAD_TIMEOUT_MS;

/**
 * Decides whether the project in the URL can be entered, loading it if needed, and makes it the current
 * project once it can (`projectActivated`, the one writer of `loadedProjectId`).
 *
 * Returns an Observable, not a Promise (B6): when a later navigation supersedes this one, the router
 * unsubscribes and nothing more happens, so a superseded navigation can never activate its project
 * after the user has moved on. Every wait is bounded by `PROJECT_GUARD_WAIT_MS`.
 */
export const projectCanMatchGuard: CanMatchFn = (
  route: Route,
  segments: UrlSegment[],
  _currentSnapshot: PartialMatchRouteSnapshot
): Observable<boolean> => {
  const store = inject(Store);
  const runtimeFlags = inject(RuntimeFeatureFlagsService);
  const loadProjectRequest = inject(LoadProjectRequest);
  const projectId = segments[0].path;

  const $projectDetailsForProject = store.selectSignal(
    selectProjectDetailsForProject(projectId)
  );
  const $currentLoadedProjectId = store.selectSignal(selectLoadedProjectId);
  const $loadingStatusForProject = store.selectSignal(
    selectLoadingStatusForProject(projectId)
  );
  const $loadingStateForProject = store.selectSignal(
    selectProjectLoadingStateForProject(projectId)
  );
  const $areReviewerStatsLoadedOrLoadingForProject = store.selectSignal(
    selectAreReviewerStatsLoadedOrLoadingForProject(projectId)
  );
  const $areMembershipStatsLoadedOrLoadingForProject = store.selectSignal(
    selectAreMembershipStatsLoadedOrLoadingForProject(projectId)
  );
  const $areProjectStatsLoadedOrLoadingForProject = store.selectSignal(
    selectAreProjectStatsLoadedOrLoadingForProject(projectId)
  );

  const $memberStatus = store.selectSignal(
    selectMemberStatusForProject(projectId)
  );

  if (
    $memberStatus() === ProjectMemberStatus.Idle ||
    $memberStatus() === ProjectMemberStatus.Error
  ) {
    loadProjectRequest.dispatchRequest.loadProject({
      projectId,
    });
  }

  const timedOut = (): Observable<boolean> => {
    store.dispatch(
      projectUiActions.projectLoadTimedOut({
        projectId,
        error: `The project did not load within ${PROJECT_GUARD_WAIT_MS / 1000} seconds.`,
      })
    );
    return of(false);
  };

  const onIsMember = (): Observable<boolean> =>
    from(runtimeFlags.initialSnapshotReady).pipe(
      switchMap(() => {
        // Dispatch stat loading actions if needed. With the own-progress consumer on AND the
        // destination the Project Overview, ProjectOverviewComponent's ProjectReviewerProgressState is
        // the only reader of this endpoint; it restores the legacy load itself on rollback or a
        // legacy-shaped answer. ProjectReviewerProgressState is provided only there, so every other
        // project child route (e.g. `stage/:id/review/completed`, `stage/:id/review/:studyId`, which
        // read the global reviewer selectors directly) keeps the legacy load regardless of the flag.
        if (
          !(
            (store.selectSignal(selectProjectReviewerProgressEnabled)() &&
              isProjectOverviewDestination(segments)) ||
            (store.selectSignal(selectStageReviewerProgressEnabled)() &&
              isStageReviewDestination(segments))
          ) &&
          !$areReviewerStatsLoadedOrLoadingForProject()
        ) {
          store.dispatch(
            projectDetailActions.retrieveProjectReviewerStats({
              projectId,
              correlationId: uuid(),
            })
          );
        }
        if (
          !store.selectSignal(selectScreeningOverviewEnabled)() &&
          !store.selectSignal(selectStageOverviewBundleEnabled)() &&
          !store.selectSignal(selectScreeningInfoEnabled)() &&
          !store.selectSignal(selectStageReviewerProgressEnabled)() &&
          (!$areMembershipStatsLoadedOrLoadingForProject() ||
            !$areProjectStatsLoadedOrLoadingForProject())
        ) {
          store.dispatch(
            projectDetailActions.retrieveFullProjectStats({
              projectId,
              correlationId: uuid(),
            })
          );
        }

        // Cached details count only while the project's last load did not fail. After a failed or refused
        // load (Error or NotFound, e.g. a background reload answered 403) any details in the store are stale,
        // so the guard loads the project again and decides on the outcome of that request alone: success
        // admits; a failure, a refusal or an ignored request denies. Stale details never admit a user the
        // server has refused, even if the new load then fails transiently.
        if (
          $loadingStatusForProject() === ProjectLoadingStatus.Error ||
          $loadingStatusForProject() === ProjectLoadingStatus.NotFound
        ) {
          return from(loadProjectRequest.dispatchRequest.loadProjectPromise({ projectId })).pipe(
            map((outcome) => outcome.status === RequestOutcome.success && $projectDetailsForProject() !== null),
            timeout({ first: PROJECT_GUARD_WAIT_MS, with: timedOut })
          );
        }
        // A load of it is already in flight after a failure or refusal (e.g. a SignalR reconnect reload
        // after a 403): the details are just as stale, so only that load's success admits. Consistent with
        // the reload rule in `LoadProjectRequest`, which never revives a project from such a load.
        const loadingState = $loadingStateForProject();
        const staleDetails =
          loadingState?.status === ProjectLoadingStatus.Loading && loadingState.afterFailure === true;
        if (!staleDetails && $currentLoadedProjectId() === projectId && $projectDetailsForProject()) {
          return of(true);
        }
        // Load the project unless a load of it is already in flight (another caller started it).
        if ($loadingStatusForProject() !== ProjectLoadingStatus.Loading) {
          loadProjectRequest.dispatchRequest.loadProject({
            projectId,
          });
        }

        // Wait for either success (data appears) or failure (error status). With stale details, success
        // means the load answered (Loaded), not merely that details are in the store.
        return combineLatest([
          store.select(selectProjectDetailsForProject(projectId)),
          store.select(selectLoadingStatusForProject(projectId)),
        ]).pipe(
          filter(
            ([details, loadingStatus]) =>
              (staleDetails ? loadingStatus === ProjectLoadingStatus.Loaded : details !== null) ||
              loadingStatus === ProjectLoadingStatus.Error ||
              loadingStatus === ProjectLoadingStatus.NotFound
          ),
          // Deny access if the load failed; allow it once the details are in the store.
          map(
            ([details, loadingStatus]) =>
              loadingStatus !== ProjectLoadingStatus.Error &&
              loadingStatus !== ProjectLoadingStatus.NotFound &&
              details !== null
          ),
          take(1),
          timeout({ first: PROJECT_GUARD_WAIT_MS, with: timedOut })
        );
      })
    );

  const decide = (memberStatus: ProjectMemberStatus): Observable<boolean> =>
    memberStatus === ProjectMemberStatus.Member
      ? onIsMember()
      : memberStatus === ProjectMemberStatus.NotMember ||
          memberStatus === ProjectMemberStatus.NotFound ||
          memberStatus === ProjectMemberStatus.Error
        ? of(false)
        : throwErrorExp('Unexpected member status');

  return store.select(selectMemberStatusForProject(projectId)).pipe(
    filter((memberStatus) => memberStatus !== ProjectMemberStatus.Loading),
    take(1),
    timeout({ first: PROJECT_GUARD_WAIT_MS, with: timedOut }),
    switchMap((memberStatus) =>
      typeof memberStatus === 'boolean' ? of(memberStatus) : decide(memberStatus)
    ),
    tap((canAccess) => {
      if (canAccess && $currentLoadedProjectId() !== projectId) {
        store.dispatch(projectUiActions.projectActivated({ projectId }));
      }
    })
  );
};
